import { supabase } from '../../../shared/lib/supabaseClient';

// Students aren't Supabase Auth users: they sign in against their students row (student_login in
// schema.sql) and get back a session token, kept here in the browser. Every student_* / secretary_*
// database function takes that token to know who is asking.
const TOKEN_KEY = 'pawigrade:student-session';
const listeners = new Set();

export function getStudentToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStudentToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage can be blocked; the session then only lasts until the page reloads.
  }
  listeners.forEach((listener) => listener(token));
}

// Called with the new token (or null once signed out or expired).
export function onStudentTokenChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Resolves to the new token, or null when no student has that username and password (it may be a
// teacher's login ID instead, see authService.signInWithUsername).
export async function studentSignIn(username, password) {
  const { data: token, error } = await supabase.rpc('student_login', {
    p_username: username,
    p_password: password,
  });
  if (error) throw error;
  if (!token) return null;
  setStudentToken(token);
  return token;
}

export async function studentSignOut() {
  const token = getStudentToken();
  setStudentToken(null);
  if (token) {
    // Best effort: the token is already gone from this browser either way. (rpc() returns a
    // thenable query builder, not a Promise, so it has no .catch().)
    try {
      await supabase.rpc('student_logout', { p_token: token });
    } catch {
      // Ignored for the reason above.
    }
  }
}

// Calls one of the student_* / secretary_* functions with the signed-in student's token. An expired
// session signs the student out, which sends them back to the login page.
export async function callStudentRpc(fn, params = {}) {
  const token = getStudentToken();
  if (!token) throw new Error('Your session has expired. Please sign in again.');

  const { data, error } = await supabase.rpc(fn, { p_token: token, ...params });
  if (error) {
    if (error.hint === 'student_session_expired') setStudentToken(null);
    throw error;
  }
  return data;
}
